import { Extension } from '@codemirror/state'
import { EditorView } from '@codemirror/view'
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language'
import { tags as t } from '@lezer/highlight'

/**
 * LaTeX Syntax Highlighting Theme
 * Provides color coding for LaTeX commands, comments, math, and other elements
 * Similar to Overleaf's syntax highlighting
 * 
 * Note: StreamLanguage tokens are styled using CSS class selectors that match
 * the token type names returned by the tokenizer (e.g., 'keyword', 'comment', etc.)
 */

// Light theme colors for LaTeX
const lightThemeColors = {
  // LaTeX commands (like \documentclass, \usepackage) - Blue
  command: '#0066CC',
  // LaTeX environments (like \begin, \end) - Darker Blue
  environment: '#0066AA',
  // Comments - Green
  comment: '#008000',
  // Math delimiters ($, $$) - Red/Orange
  mathDelimiter: '#D73A49',
  // Math content - Teal/Blue
  mathContent: '#005CC5',
  // Math operators (+, -, =, etc.) - Purple
  mathOperator: '#6F42C1',
  // Braces and brackets - Brown/Orange
  bracket: '#E36209',
  // Optional parameters [ ] - Darker Orange
  optional: '#C24E00',
  // Labels and references - Purple/Magenta
  label: '#6F42C1',
  reference: '#8250DF',
  citation: '#8250DF',
  // Numbers - Blue
  number: '#005CC5',
  // Strings/Text in optional params - Dark Gray
  string: '#032F62',
}

// Dark theme colors for LaTeX
const darkThemeColors = {
  // LaTeX commands - Light Blue
  command: '#79B8FF',
  // LaTeX environments - Cyan
  environment: '#56D4DD',
  // Comments - Green
  comment: '#6A9955',
  // Math delimiters - Orange/Red
  mathDelimiter: '#FF7B72',
  // Math content - Light Blue
  mathContent: '#79B8FF',
  // Math operators - Purple
  mathOperator: '#D2A8FF',
  // Braces and brackets - Orange
  bracket: '#FFA657',
  // Optional parameters - Yellow/Orange
  optional: '#FFA657',
  // Labels and references - Purple
  label: '#D2A8FF',
  reference: '#BC8CFF',
  citation: '#BC8CFF',
  // Numbers - Light Blue
  number: '#79B8FF',
  // Strings/Text in optional params - Light Gray
  string: '#C9D1D9',
}

/**
 * Create a LaTeX syntax highlighting theme
 * @param isDark - Whether to use dark theme colors
 */
export function latexTheme(isDark: boolean = false): Extension {
  const colors = isDark ? darkThemeColors : lightThemeColors

  // Define base highlighting style for standard token types
  // StreamLanguage tokens map to these base types
  const highlightStyle = HighlightStyle.define([
    // Comments
    { tag: t.comment, color: colors.comment, fontStyle: 'italic' },
    
    // LaTeX commands (keywords)
    { tag: t.keyword, color: colors.command },
    
    // Strings (math delimiters)
    { tag: t.string, color: colors.mathDelimiter },
    
    // Numbers
    { tag: t.number, color: colors.number },
    
    // Operators
    { tag: t.operator, color: colors.mathOperator },
    
    // Brackets
    { tag: t.bracket, color: colors.bracket },
    
    // Attributes (optional parameters)
    { tag: t.attributeName, color: colors.optional },
    
    // Atoms (labels, references)
    { tag: t.atom, color: colors.label },
    
    // Variables (math variables)
    { tag: t.variableName, color: colors.mathContent },
  ])

  // Create custom styling using EditorView.theme
  // StreamLanguage creates CSS classes based on token type names
  // We use attribute selectors to target tokens with specific classes
  const customTheme = EditorView.theme({
    '&': {
      color: isDark ? '#C9D1D9' : '#24292E',
    },
    // Base LaTeX commands (keywords)
    '.cm-keyword': {
      color: colors.command,
      fontWeight: '500',
    },
    // Comments
    '.cm-comment': {
      color: colors.comment,
      fontStyle: 'italic',
    },
    // Math delimiters (strings)
    '.cm-string': {
      color: colors.mathDelimiter,
    },
    // Math content (variables)
    '.cm-variable-2': {
      color: colors.mathContent,
    },
    // Math operators
    '.cm-operator': {
      color: colors.mathOperator,
    },
    // Numbers
    '.cm-number': {
      color: colors.number,
    },
    // Brackets
    '.cm-bracket': {
      color: colors.bracket,
    },
    // Attributes (optional parameters)
    '.cm-attributeName': {
      color: colors.optional,
    },
    // Atoms (labels, references, citations)
    '.cm-atom': {
      color: colors.label,
    },
  }, { dark: isDark })

  return [
    syntaxHighlighting(highlightStyle),
    customTheme,
  ]
}

